import React, { useRef, useState } from 'react';

import { twMerge } from 'tailwind-merge';
import EmojiPicker from './EmojiPicker';
import { isTelavoxBrand } from 'flow/utils/brand-utils';
import { getBrandKey } from 'flow/brands/brands';
import { getEmojiName } from './reaction-utils';
import useAppContext from 'flow/hooks/useAppContext';

interface Props {
    myReactions: string[];
    onSelect: (emoji: string) => void;
    className?: string;
    /** Dark variant, see MessagesPage. */
    videoDark?: boolean;
}

const ReactionPicker = ({
    className,
    onSelect,
    myReactions,
    videoDark = false,
}: Props) => {
    const { i18next } = useAppContext();
    const isLocalhost = window.location.hostname.includes('localhost');
    const isTelavox = isTelavoxBrand(getBrandKey());

    const heart = isTelavox || isLocalhost ? ':green_heart:' : ':heart:';

    const EMOJIS = [
        ':+1:',
        ':tada:',
        ':eyes:',
        ':joy:',
        heart,
        ':white_check_mark:',
    ];

    const quickReactions = (emoji: string) => {
        onSelect(emoji);
    };

    const [keepOpen, setKeepOpen] = useState(true);
    const pickerRef = useRef(null);

    return (
        <div
            className={twMerge('flex', className)}
            ref={pickerRef}
            onMouseEnter={() => setKeepOpen(true)}
            onMouseLeave={(event) => {
                if (
                    pickerRef.current &&
                    event.relatedTarget instanceof Node &&
                    !pickerRef.current.contains?.(event.relatedTarget)
                ) {
                    setKeepOpen(false);
                }
            }}
        >
            {EMOJIS.map((emoji) => (
                <button
                    key={emoji}
                    aria-label={i18next.t('action.react_with', {
                        emoji: getEmojiName(i18next, emoji),
                    })}
                    className={twMerge(
                        'hidden rounded-sm border px-2 py-1 text-white lg:block',
                        videoDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200',
                        myReactions.includes(emoji)
                            ? 'border-green bg-green/10 hover:bg-green/20'
                            : 'border-transparent',
                    )}
                    onClick={() => quickReactions(emoji)}
                >
                    <em-emoji shortcodes={emoji} />
                </button>
            ))}

            <EmojiPicker
                onEmojiSelect={onSelect}
                keepOpen={keepOpen}
                videoDark={videoDark}
            />
        </div>
    );
};

export default ReactionPicker;
